import { useEffect, useRef, useCallback } from "react"
import { useConnectionState, useTrackToggle } from "@livekit/components-react"
import { Track } from "livekit-client"
import { ConnectionState } from "livekit-client"
import { useSelector } from "react-redux"
import { hasRole } from "../../../data/utility/ability"

const MAX_RETRY_ATTEMPTS = 2
const RETRY_DELAY_MS = 2000
const INITIAL_DELAY_MS = 3000
const TOGGLE_MIN_INTERVAL_MS = 600

export function MuteCurrentUser({ isSilentMode }) {
    const { toggle, enabled } = useTrackToggle({
        source: Track.Source.Microphone,
    })

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- le store legacy n'est pas typé
    const currentUser = useSelector((state: any) => state.data.currentUser?.object)

    const connectionState = useConnectionState()
    const isConnected = connectionState?.toString() === ConnectionState.Connected

    const isSoundMode = !isSilentMode

    // Keep a ref of the latest isSoundMode so delayed callbacks always
    // read the current value instead of a stale closure capture.
    const isSoundModeRef = useRef(isSoundMode)
    isSoundModeRef.current = isSoundMode

    // Refs to prevent race conditions and cleanup timeouts
    const isTogglingRef = useRef(false)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)
    const enableSafetyTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const retryCountRef = useRef(0)
    const lastTargetStateRef = useRef<boolean | null>(null)
    const queuedTargetRef = useRef<boolean | null>(null)

    // Tracks whether the mic was successfully enabled at least once during
    // the current dialogue period. Distinguishes "toggle failed silently"
    // (micActivatedRef = false → retry) from "student manually muted"
    // (micActivatedRef = true → don't retry).
    const micActivatedRef = useRef(false)

    const clearPendingTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
    }, [])

    // Throttle: track last native toggle time to enforce minimum interval.
    // iOS audio session silently drops rapid mic on/off cycles.
    const lastToggleTimeRef = useRef(0)
    const throttleTimerRef = useRef<NodeJS.Timeout | null>(null)

    const safeToggle = useCallback(
        async (targetState: boolean, isRetry = false) => {
            // If already toggling, queue the latest target instead of dropping
            if (isTogglingRef.current) {
                queuedTargetRef.current = targetState
                return
            }

            // Skip if target state hasn't changed and this isn't a retry
            if (!isRetry && lastTargetStateRef.current === targetState) {
                return
            }

            // Throttle: enforce minimum interval between native toggle() calls.
            // iOS audio session silently drops rapid mic toggles; applied on all
            // platforms as a safety measure (600ms delay only during spamming).
            if (!isRetry) {
                const timeSinceLast = Date.now() - lastToggleTimeRef.current
                if (timeSinceLast < TOGGLE_MIN_INTERVAL_MS) {
                    if (throttleTimerRef.current) clearTimeout(throttleTimerRef.current)
                    throttleTimerRef.current = setTimeout(() => {
                        throttleTimerRef.current = null
                        safeToggle(isSoundModeRef.current)
                    }, TOGGLE_MIN_INTERVAL_MS - timeSinceLast)
                    return
                }
            }

            // Reset retry count for new target state
            if (!isRetry) {
                retryCountRef.current = 0
            }

            // Check max retries
            if (isRetry && retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
                return
            }

            isTogglingRef.current = true
            lastTargetStateRef.current = targetState
            lastToggleTimeRef.current = Date.now()
            queuedTargetRef.current = null // Clear queue, we're handling this target

            try {
                await toggle(targetState)
            } catch (error) {
                console.error("MuteCurrentUser: toggle error", error)
                retryCountRef.current++

                if (retryCountRef.current < MAX_RETRY_ATTEMPTS) {
                    clearPendingTimeout()
                    timeoutRef.current = setTimeout(() => {
                        safeToggle(targetState, true)
                    }, RETRY_DELAY_MS)
                    return // Don't process queue during retry scheduling
                }
            } finally {
                isTogglingRef.current = false
            }

            // After toggle completes, process queued state if different
            if (queuedTargetRef.current !== null) {
                const queued = queuedTargetRef.current
                queuedTargetRef.current = null
                safeToggle(queued)
            }
        },
        [toggle, clearPendingTimeout]
    )

    // Initial toggle after connection stabilizes.
    // Reads isSoundModeRef.current at execution time to avoid stale closures
    // when sound mode changes during the delay (e.g. reconnection + mode switch).
    // On reconnect, LiveKit internally restores the mic track — only toggle if
    // the mic state doesn't match sound mode after the delay, to avoid a
    // concurrent senderReplaceTrack call that crashes the native WebRTC module.
    const enabledRef = useRef(enabled)
    enabledRef.current = enabled

    useEffect(() => {
        if (!isConnected || !hasRole(currentUser, "student")) return

        clearPendingTimeout()
        lastTargetStateRef.current = null
        timeoutRef.current = setTimeout(() => {
            const target = isSoundModeRef.current
            // Skip if mic is already in the correct state (LiveKit may have
            // restored the track automatically during reconnect)
            if (enabledRef.current === target) return
            safeToggle(target)
        }, INITIAL_DELAY_MS)

        return clearPendingTimeout
        // eslint-disable-next-line react-hooks/exhaustive-deps -- déplacé tel quel : ses dépendances sont celles d'avant
    }, [isConnected])

    // React to sound mode changes
    useEffect(() => {
        if (!isConnected || !hasRole(currentUser, "student")) return

        // Reset activation tracking when entering dialogue mode
        if (isSoundMode) {
            micActivatedRef.current = false
        }

        // Cancel any pending initial toggle — sound mode change takes priority
        clearPendingTimeout()
        safeToggle(isSoundMode)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- déplacé tel quel : ses dépendances sont celles d'avant
    }, [isSoundMode])

    // Track when mic successfully enables during dialogue mode
    useEffect(() => {
        if (enabled && isSoundMode) {
            micActivatedRef.current = true
        }
    }, [enabled, isSoundMode])

    // Force disable if mic gets enabled when it shouldn't be (silent mode)
    useEffect(() => {
        if (!enabled || isSoundMode) return
        if (!isConnected || !hasRole(currentUser, "student")) return

        // Small delay to avoid conflicting with other toggles
        clearPendingTimeout()
        timeoutRef.current = setTimeout(() => {
            if (enabled && !isSoundModeRef.current) {
                lastTargetStateRef.current = null // Reset to prevent skip
                safeToggle(false)
            }
        }, 500)

        return clearPendingTimeout
        // eslint-disable-next-line react-hooks/exhaustive-deps -- déplacé tel quel : ses dépendances sont celles d'avant
    }, [enabled, isSoundMode, isConnected])

    // Force enable if mic stays off when it should be on (dialogue mode).
    // On mobile, toggle(true) can resolve without error but fail to actually
    // publish the mic track (audio session not ready, iOS interruption, etc.).
    // Only retries if the mic was never successfully enabled during this dialogue
    // period (micActivatedRef = false), so it won't override a student's manual mute.
    // Uses a separate timeout ref so it doesn't cancel the initial toggle.
    useEffect(() => {
        if (enableSafetyTimeoutRef.current) {
            clearTimeout(enableSafetyTimeoutRef.current)
            enableSafetyTimeoutRef.current = null
        }

        if (enabled || !isSoundMode) return
        if (!isConnected || !hasRole(currentUser, "student")) return

        // If mic was already enabled once during this dialogue period,
        // the student likely muted manually — don't override
        if (micActivatedRef.current) return

        enableSafetyTimeoutRef.current = setTimeout(() => {
            if (isSoundModeRef.current && !micActivatedRef.current) {
                lastTargetStateRef.current = null // Reset to prevent skip
                safeToggle(true)
            }
        }, 1500)

        return () => {
            if (enableSafetyTimeoutRef.current) {
                clearTimeout(enableSafetyTimeoutRef.current)
                enableSafetyTimeoutRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- déplacé tel quel : ses dépendances sont celles d'avant
    }, [enabled, isSoundMode, isConnected])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearPendingTimeout()
            if (enableSafetyTimeoutRef.current) {
                clearTimeout(enableSafetyTimeoutRef.current)
                enableSafetyTimeoutRef.current = null
            }
            if (throttleTimerRef.current) {
                clearTimeout(throttleTimerRef.current)
                throttleTimerRef.current = null
            }
        }
    }, [clearPendingTimeout])

    return null
}
